import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { api } from '../../../lib/api';

export type KorrigierbarerEintrag = {
  id: string | number;
  description: string;
  details?: string | null;
};

type Props = {
  personId: string;
  eintrag: KorrigierbarerEintrag | null;
  onClose: () => void;
  onKorrigiert: () => void;
};

/**
 * Historieneintrag korrigieren (T-0077).
 *
 * Die Akte ist unveränderlich: Die Korrektur wird ein neuer Eintrag mit Grund,
 * der alte bleibt sichtbar und wird als „korrigiert“ markiert.
 */
export default function HistoryCorrectionDialog({ personId, eintrag, onClose, onKorrigiert }: Props) {
  const [beschreibung, setBeschreibung] = useState('');
  const [details, setDetails] = useState('');
  const [grund, setGrund] = useState('');
  const [speichert, setSpeichert] = useState(false);

  useEffect(() => {
    setBeschreibung(eintrag?.description || '');
    setDetails(eintrag?.details || '');
    setGrund('');
  }, [eintrag]);

  const speichern = async () => {
    if (!eintrag) return;
    setSpeichert(true);
    try {
      await api.post(`/api/personen/${personId}/history/${eintrag.id}/correct`, {
        description: beschreibung.trim(),
        details: details.trim() || null,
        reason: grund.trim(),
      });
      toast.success('Korrektur gespeichert');
      onKorrigiert();
      onClose();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Die Korrektur konnte nicht gespeichert werden');
    } finally {
      setSpeichert(false);
    }
  };

  return (
    <Dialog open={eintrag !== null} onOpenChange={(offen) => (!offen ? onClose() : undefined)}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Eintrag korrigieren</DialogTitle>
          <DialogDescription>
            Der ursprüngliche Eintrag bleibt erhalten und wird als korrigiert markiert.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label htmlFor="korrektur-beschreibung" className="text-sm font-medium">Beschreibung</label>
            <Input
              id="korrektur-beschreibung"
              value={beschreibung}
              maxLength={500}
              onChange={(e) => setBeschreibung(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="korrektur-details" className="text-sm font-medium">Details</label>
            <Textarea
              id="korrektur-details"
              value={details}
              rows={4}
              onChange={(e) => setDetails(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="korrektur-grund" className="text-sm font-medium">Grund der Korrektur</label>
            <Input
              id="korrektur-grund"
              value={grund}
              maxLength={500}
              placeholder="z. B. falsches Datum eingetragen"
              onChange={(e) => setGrund(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={speichert}>Abbrechen</Button>
          <Button onClick={() => void speichern()} disabled={speichert || !beschreibung.trim() || !grund.trim()}>
            Korrektur speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
